import { RootState } from '../store';
import { getQueryParameters, regionApi, regionRequest } from './regionApi';

import { MY_ACCOUNT_LIST } from '../utils/helper';

/* -------------------------------------------------- */
/* Helpers                                            */
/* -------------------------------------------------- */

const getRegion = (state: RootState, regionKey?: string) =>
  regionKey ? state.auth.regions?.[regionKey] : null;

const fail = (msg: string) => ({
  error: {
    status: 500,
    statusText: 'Internal RTK Query Error',
    data: msg
  }
});

/* -------------------------------------------------- */
/* API                                                */
/* -------------------------------------------------- */

export const regionInstanceApi = regionApi.injectEndpoints({
  endpoints: (build) => ({
    /* ================================================== */
    /* Instance by account (with optional historic data)  */
    /* ================================================== */
    getInstByAccount: build.query<any, Record<string, any>>({
      async queryFn(arg, _api, _extraOptions, fetchWithBQ) {
        const account = arg?.account;

        if (!account?.region?.token || !account?.account_id) {
          return fail('Region token or account missing');
        }

        const baseReq = (await fetchWithBQ(
          regionRequest(account.region, 'instance/get_instance_info', {
            method: 'POST',
            body: getQueryParameters(account)
          })
        )) as any;

        if (baseReq.error) return baseReq;

        let instance: any = {
          ...baseReq.data,
          region: account.region
        };

        if (arg?.queryHistoricData) {
          const histReq = await fetchWithBQ(
            regionRequest(
              account.region,
              'instance/get_instance_historic_quota',
              {
                method: 'POST',
                body: getQueryParameters(account)
              }
            )
          );

          if (!histReq.error && histReq.data) {
            const h = histReq.data as any;

            instance = {
              ...instance,
              quota_used_last_24_hours: h?.quota_used_last_24_hours,
              is_average_quota_used_last_6_days:
                h?.is_average_quota_used_last_6_days,
              quotaHistoryData: h
            };
          }
        }

        return {
          data: getQueryParameters(account, {
            data: instance
          })
        };
      },

      providesTags: (result, error, arg) => [
        {
          type: 'Instance',
          id: arg?.account?.group_id || MY_ACCOUNT_LIST
        },
        { type: 'Instance' }
      ]
    }),

    /* ================================================== */
    /* Access URL                                         */
    /* ================================================== */
    getInstanceAccessUrl: build.query<any, Record<string, any>>({
      query: ({ account }) =>
        regionRequest(account?.region, 'instance/get_instance_access_url', {
          method: 'POST',
          body: getQueryParameters(account)
        }),
      keepUnusedDataFor: 5
    }),

    /* ================================================== */
    /* Provision Status                                   */
    /* ================================================== */
    getProvisionStatus: build.query<any, Record<string, any>>({
      async queryFn(arg, api, _extra, fetchWithBQ) {
        const state = api.getState() as RootState;

        const region = getRegion(state, arg?.region);

        if (!region || !arg?.taskId || !arg?.accountIds) {
          return fail('Missing region, taskId or accountIds');
        }

        return fetchWithBQ(
          regionRequest(region, 'instance/get_provision_status', {
            method: 'POST',
            body: {
              task_id: arg.taskId,
              account_id: arg.accountIds?.[0] || ''
            }
          })
        );
      },

      providesTags: (_r, _e, arg) => [
        {
          type: 'Provision',
          id: arg?.taskId
        }
      ]
    }),

    /* ================================================== */
    /* Provision Instance                                 */
    /* ================================================== */
    provisionInstance: build.mutation<any, Record<string, any>>({
      async queryFn(arg, api, _e, fetchWithBQ) {
        const state = api.getState() as RootState;

        const region = getRegion(state, arg?.region);

        if (!region) {
          return fail('Region token or url missing');
        }

        return fetchWithBQ(
          regionRequest(region, 'instance/provision_instance', {
            method: 'POST',
            body: {
              account_id: arg?.accountIds?.[0] || '',
              tz_code: arg?.tzCode || '',
              image_version: arg?.imageVersion || '',
              adom_version: arg?.adomVersion || ''
            }
          })
        );
      }
    }),

    /* ================================================== */
    /* Resize / Upgrade / Reboot (patternized)            */
    /* ================================================== */
    instanceResize: build.mutation<any, any>({
      async queryFn(arg, api, _e, fetchWithBQ) {
        const region = getRegion(api.getState() as RootState, arg?.region);

        if (!region) return fail('Region token or url missing');

        return fetchWithBQ(
          regionRequest(region, 'instance/resize', {
            method: 'POST',
            body: {
              account_id: arg?.accountIds?.[0] || '',
              eta_mins: arg?.etaMins || 0
            }
          })
        );
      }
    }),

    /* ================================================== */
    /* Upgrade                                           */
    /* ================================================== */
    instanceUpgrade: build.mutation<any, any>({
      async queryFn(arg, api, _e, fetchWithBQ) {
        const region = getRegion(api.getState() as RootState, arg?.region);

        if (!region) return fail('Region token or url missing');

        return fetchWithBQ(
          regionRequest(region, 'instance/upgrade', {
            method: 'POST',
            body: {
              account_id: arg?.accountIds?.[0] || '',
              group_id: arg?.groupId || '',
              to_version: arg?.to_version || '',
              eta_mins: arg?.etaMins || 0
            }
          })
        );
      }
    }),

    /* ================================================== */
    /* Instance state                                    */
    /* ================================================== */
    getInstanceState: build.query<any, any>({
      async queryFn(arg, api, _e, fetchWithBQ) {
        const region = getRegion(api.getState() as RootState, arg?.region);

        if (!region) return fail('Missing region config');

        return fetchWithBQ(
          regionRequest(region, 'instance/get_instance_state', {
            method: 'POST',
            body: {
              account_id: arg?.accountIds?.[0] || '',
              action: arg?.action,
              group_id: arg?.groupId || ''
            }
          })
        );
      }
    }),

    /* ================================================== */
    /* VM Status                                         */
    /* ================================================== */
    getVmStatus: build.query<any, any>({
      query: ({ account }) =>
        regionRequest(account?.region, 'instance/get_vm_status', {
          method: 'POST',
          body: getQueryParameters(account)
        })
    }),

    /* ================================================== */
    /* Instance Extra Info                               */
    /* ================================================== */
    getInstanceExtraInfo: build.query<any, any>({
      async queryFn(arg, _api, _e, fetchWithBQ) {
        const account = arg?.account;

        if (!account?.region?.token) return fail('Missing token');

        const vmStatus = (await fetchWithBQ(
          regionRequest(account.region, 'instance/get_vm_status', {
            method: 'POST',
            body: getQueryParameters(account)
          })
        )) as any;

        if (vmStatus?.data?.is_active) {
          return fetchWithBQ(
            regionRequest(
              account.region,
              'instance/get_active_instance_extra_info',
              {
                method: 'POST',
                body: getQueryParameters(account)
              }
            )
          );
        }

        return { data: vmStatus.data };
      }
    }),

    /* ================================================== */
    /* Reboot                                            */
    /* ================================================== */
    instanceReboot: build.mutation<any, any>({
      query: ({ account }) =>
        regionRequest(account?.region, 'instance/reboot_instance', {
          method: 'POST',
          body: {
            account_id: account?.account_id || ''
          }
        })
    }),

    /* ================================================== */
    /* Firmware Download                                 */
    /* ================================================== */
    firmwareDownload: build.mutation<any, any>({
      async queryFn(arg, api, _e, fetchWithBQ) {
        const account = arg?.account;

        const region = getRegion(
          api.getState() as RootState,
          account?.region?.alias
        );

        if (!region) return fail('Missing region');

        const result = await fetchWithBQ(
          regionRequest(region, 'firmware_download', {
            method: 'GET',
            params: {
              version: arg?.firmwareVersion,
              platform: arg?.platform
            },
            responseHandler: (r) => r.blob()
          })
        );

        if (result.error) return result;

        const blobUrl = URL.createObjectURL(result.data as Blob);

        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = arg?.firmwareVersion || '';
        a.click();

        return { data: null };
      }
    })
  })
});

export const {
  useGetInstByAccountQuery,
  useGetInstanceAccessUrlQuery,
  useGetProvisionStatusQuery,
  useLazyGetInstanceAccessUrlQuery,
  useProvisionInstanceMutation,
  useInstanceResizeMutation,
  useInstanceUpgradeMutation,
  useGetInstanceStateQuery,
  useGetVmStatusQuery,
  useGetInstanceExtraInfoQuery,
  useInstanceRebootMutation,
  useFirmwareDownloadMutation
} = regionInstanceApi;
